import type { Cpc25Report, ProvisionSnapshotDetail } from '../../../../types/LegalCase';

// Geração do CSV do relatório consolidado CPC 25 (sem lib externa). Uma linha por
// entidade × polo × classificação, mais colunas de perda estimada e provisão. Puro e
// testável; o disparo do download fica no componente.

const HEADER = [
    'Entidade credora',
    'Polo',
    'Classificacao',
    'Numero de casos',
    'Perda estimada (R$)',
    'Provisao (R$)',
    'Fora do padrao',
    'Ajustados manualmente',
];

// Escapa um campo para CSV: aspas duplas ao redor e duplica aspas internas. Sempre entre
// aspas para não quebrar com vírgula/; no separador nem com o ponto/vírgula do número BR.
const escape = (value: string | number): string => {
    const s = String(value ?? '');
    return `"${s.replace(/"/g, '""')}"`;
};

// Campo de TEXTO: neutraliza injeção de fórmula antes de escapar. Célula que comece com `=`, `+`,
// `-`, `@`, tab ou CR é avaliada como fórmula quando o auditor abre o arquivo no Excel — e rótulo,
// motivo da anulação, número do processo e entidade são digitados pelo usuário (achado do Copilot na
// PR #1215). Prefixo `'` como no `ExportDropdown.tsx`. Só para texto: número vai por `escape`
// direto, senão um valor negativo viraria texto e a coluna deixaria de somar.
const FORMULA_START = /^[=+\-@\t\r]/;
const text = (value: string | null | undefined): string => {
    const s = String(value ?? '');
    return escape(FORMULA_START.test(s) ? `'${s}` : s);
};

// Número no formato BR (1234.5 -> "1234,50") sem símbolo de moeda — o Excel pt-BR entende.
const money = (value: number): string => value.toFixed(2).replace('.', ',');

type PoleTotal = {
    poleFormatted: string;
    count: number;
    estimatedLoss: number;
    provision: number;
    offSuggestion: number;
    overridden: number;
};

const emptyPoleTotal = (poleFormatted: string): PoleTotal => ({
    poleFormatted, count: 0, estimatedLoss: 0, provision: 0, offSuggestion: 0, overridden: 0,
});

// Soma os nós de polo de todas as entidades, na ordem em que o relatório os traz (Ativo, Passivo).
const totalsByPole = (report: Cpc25Report): PoleTotal[] => {
    const byPole = new Map<string, PoleTotal>();
    for (const pole of report.entities.flatMap((entity) => entity.poles)) {
        const total = byPole.get(pole.pole) ?? emptyPoleTotal(pole.poleFormatted);
        total.count += pole.count;
        total.estimatedLoss += pole.estimatedLoss;
        total.provision += pole.provision;
        total.offSuggestion += pole.offSuggestion;
        total.overridden += pole.overridden;
        byPole.set(pole.pole, total);
    }
    return Array.from(byPole.values());
};

export const buildCpc25Csv = (report: Cpc25Report): string => {
    const lines: string[] = [HEADER.map(escape).join(';')];

    for (const entity of report.entities) {
        for (const pole of entity.poles) {
            for (const row of pole.rows) {
                lines.push([
                    text(entity.creditorEntity),
                    text(pole.poleFormatted),
                    text(row.classificationFormatted),
                    escape(row.count),
                    escape(money(row.estimatedLoss)),
                    escape(money(row.provision)),
                    escape(row.offSuggestion),
                    escape(row.overridden),
                ].join(';'));
            }
        }
    }

    // Total por polo (Task 22): a perda estimada do passivo e a do ativo são naturezas distintas,
    // como na aba VALORES da planilha. Só depois vem o total geral.
    for (const total of totalsByPole(report)) {
        lines.push([
            escape('TOTAL'), text(total.poleFormatted), escape(''),
            escape(total.count), escape(money(total.estimatedLoss)), escape(money(total.provision)),
            escape(total.offSuggestion), escape(total.overridden),
        ].join(';'));
    }
    // Linha de total geral.
    const s = report.summary;
    lines.push([
        escape('TOTAL'), escape(''), escape(''),
        escape(s.count), escape(money(s.estimatedLoss)), escape(money(s.provision)),
        escape(s.offSuggestion), escape(s.overridden),
    ].join(';'));

    return lines.join('\n');
};

// Nome do arquivo com a data do dia (yyyy-mm-dd), para não sobrescrever exports anteriores.
export const cpc25CsvFilename = (today: Date = new Date()): string => {
    const iso = today.toISOString().slice(0, 10);
    return `relatorio-cpc25-${iso}.csv`;
};

// ─── Export do FECHAMENTO congelado (lacuna da Task 20, fechada na AF-3822) ──────────────────
// Os botões do relatório exportam o AO VIVO. Este exporta as linhas gravadas no fechamento, que não
// mudam quando um caso é editado depois — é o arquivo que vai à auditoria.
//
// Mesmas colunas, na mesma ordem, do "Por caso" ao vivo (`Cpc25CaseExportQuery._HEADER` no
// backend): assim a auditoria compara os dois arquivos lado a lado. Um preâmbulo identifica o
// fechamento (referência, quando foi tirado, quem, anulação) antes da tabela.
const SNAPSHOT_HEADER = [
    'Processo',
    'Entidade credora',
    'Polo',
    'Classificacao CPC 25',
    'Valor da causa',
    'Perda estimada %',
    'Perda estimada R$',
    'Provisao R$',
    'Situacao',
    'Motivo encerramento',
];

// Número cru pt-BR, vazio quando nulo — igual ao `_decimal_ptbr` do backend.
const optionalMoney = (value: number | null | undefined): string =>
    value === null || value === undefined ? '' : money(value);

const isoDay = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

const snapshotPreamble = (snapshot: ProvisionSnapshotDetail): string[] => {
    const rows: [string, string][] = [
        ['Fechamento contabil CPC 25', snapshot.label || ''],
        ['Data-base (referencia)', isoDay(snapshot.baseDate)],
        ['Tirado em', isoDay(snapshot.closedAt)],
        ['Fechado por', snapshot.closedBy || ''],
    ];
    // Fechamento anulado continua exportável (a trilha não some), mas o arquivo diz que foi anulado —
    // senão alguém manda à auditoria um número que o jurídico já declarou errado.
    if (snapshot.voided) {
        rows.push(['ANULADO em', isoDay(snapshot.voidedAt)]);
        rows.push(['Anulado por', snapshot.voidedBy || '']);
        rows.push(['Motivo da anulacao', snapshot.voidReason || '']);
    }
    return rows.map(([key, value]) => [escape(key), text(value)].join(';'));
};

export const buildSnapshotCsv = (snapshot: ProvisionSnapshotDetail): string => {
    const lines: string[] = [...snapshotPreamble(snapshot), ''];
    lines.push(SNAPSHOT_HEADER.map(escape).join(';'));
    for (const line of snapshot.lines) {
        lines.push([
            text(line.externalCaseNumber || ''),
            text(line.creditorEntity || ''),
            text(line.poleFormatted),
            text(line.classificationFormatted),
            escape(optionalMoney(line.claimValue)),
            escape(optionalMoney(line.estimatedLossPercent)),
            escape(optionalMoney(line.estimatedLossAmount)),
            escape(money(line.provisionAmount)),
            text(line.statusFormatted),
            text(line.closureReasonFormatted || ''),
        ].join(';'));
    }
    // Total a partir do CABEÇALHO gravado, não da soma das linhas: é o número que o fechamento
    // declarou (e que a tela mostra); se um dia divergir da soma, a divergência fica visível.
    lines.push([
        escape('TOTAL'), escape(String(snapshot.totalCount)), escape(''), escape(''), escape(''),
        escape(''), escape(money(snapshot.totalEstimatedLoss)), escape(money(snapshot.totalProvision)),
        escape(''), escape(''),
    ].join(';'));
    return lines.join('\n');
};

// Nome pela data-base (o período), não pela data do download: exportar o mesmo fechamento duas vezes
// dá o mesmo nome, porque é o mesmo arquivo. Anulado leva o sufixo para não ser confundido.
export const snapshotCsvFilename = (snapshot: ProvisionSnapshotDetail): string => {
    const ref = isoDay(snapshot.baseDate) || isoDay(snapshot.closedAt) || 'sem-data';
    return `fechamento-cpc25-ref-${ref}${snapshot.voided ? '-ANULADO' : ''}.csv`;
};
